import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart2 } from 'lucide-react';
import {
  NavigationMenuItem,
  NavigationMenuTriggerWithFix,
  NavigationMenuContent,
} from '../ui/navigation-menu';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import { api } from '../../api';
import { AppContext } from '../../hooks/AppProvider';
import { EUserTier } from '../../interfaces';

// Attribution quota interface
interface AttributionQuota {
  used: number;
  total: number;
  percentage: number;
  tier: EUserTier;
}

type AttributionQuotaProps = {
  used: number;
  total: number;
  percentage: number;
  className?: string;
};

// Custom hook for attribution quota using React Query
export const useAttributionQuota = () => {
  const { user } = useContext(AppContext);

  return useQuery<AttributionQuota>({
    queryKey: ['attributionQuota', user?.email],
    queryFn: async () => {
      const response = await api.auth.getCurrentUsage();
      return response;
    },
    enabled: !!user, // Only fetch when user is logged in
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Format number to be more readable (e.g., 1k, 10k, etc.)
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${Math.floor(num / 100000) / 10}M`;
  }
  if (num >= 1000) {
    return `${Math.floor(num / 100) / 10}k`;
  }
  return num.toString();
};

// Base component that can be used anywhere
export const AttributionQuotaBase = ({
  used,
  total,
  percentage,
  className,
}: AttributionQuotaProps) => {
  const formattedUsed = formatNumber(used);
  const formattedTotal = formatNumber(total);

  const getProgressColorClassBasedOnPercentage = () => {
    if (percentage < 50) {
      return 'bg-emerald-500';
    }
    if (percentage < 75) {
      return 'bg-amber-500';
    }
    return 'bg-destructive';
  };

  return (
    <div
      className={cn(
        'w-64 space-y-2 bg-popover text-popover-foreground',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Attribution Usage</span>
        <span className="text-xs text-muted-foreground">
          {formattedUsed} / {formattedTotal}
        </span>
      </div>
      <div className="relative">
        <Progress value={percentage} className="h-2" />
        <div
          className={cn(
            'absolute inset-0 h-2 transition-all',
            getProgressColorClassBasedOnPercentage(),
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="pt-1 text-xs text-muted-foreground">
        {used.toLocaleString()} of {total.toLocaleString()} attributions used
        this month
      </p>
    </div>
  );
};

// Navigation-specific wrapper with React Query integration
export const AttributionQuotaIndicator = () => {
  const { user } = useContext(AppContext);
  const { data: attributionQuota, isLoading, error } = useAttributionQuota();

  // Don't render if no user or no data
  if (!user || !attributionQuota || isLoading || error) {
    return null;
  }

  const formattedUsed = formatNumber(attributionQuota.used);
  const formattedTotal = formatNumber(attributionQuota.total);

  return (
    <NavigationMenuItem>
      <NavigationMenuTriggerWithFix className="h-9 gap-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary">
        <BarChart2 className="h-4 w-4 text-muted-foreground" />
        <span>
          {formattedUsed} / {formattedTotal}
        </span>
      </NavigationMenuTriggerWithFix>
      <NavigationMenuContent className="left-auto right-0 p-3">
        <AttributionQuotaBase
          used={attributionQuota.used}
          total={attributionQuota.total}
          percentage={attributionQuota.percentage}
        />
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};
